import { PLUGIN_ENTITY_ICON } from "metabase/hooks/plugins";
import {
  PLUGIN_COLLECTIONS,
  PLUGIN_COLLECTION_COMPONENTS,
  PLUGIN_CONTENT_VERIFICATION,
  PLUGIN_MODERATION,
} from "metabase/plugins";
import { PLUGIN_SEARCH_FILTERS } from "metabase/search/plugins";
import MetabaseSettings from "metabase/utils/settings";

import { MetabotVerifiedContentConfigurationPane } from "./components/MetabotVerifiedContentConfigurationPane/MetabotVerifiedContentConfigurationPane";
import {
  EntityModerationIcon,
  ModerationStatusIcon,
} from "./components/ModerationIcons/ModerationIcons";
import {
  ModerationReviewTextForDashboard,
  ModerationReviewTextForQuestion,
} from "./components/ModerationReviewText/ModerationReviewText";
import {
  CollectionAuthorityLevelDisplay,
  CollectionAuthorityLevelIcon,
  FormCollectionAuthorityLevelPicker,
  getAuthorityLevelMenuItems,
} from "./components/OfficialCollection/OfficialCollection";
import {
  MetricFilterControls,
  ModelFilterControls,
  VerifiedFilter,
  getDefaultMetricFilters,
  getDefaultModelFilters,
} from "./components/VerifiedFilters/VerifiedFilters";
import { OFFICIAL_COLLECTION } from "./constants";
import {
  createUseGetIcon,
  useCardMenuItems,
  useDashboardMenuItems,
  useQuestionMenuItems,
} from "./hooks";
import {
  getModerationTimelineEvents,
  getStatusIcon,
  isOfficialCollection,
} from "./utils";

/**
 * Official collections and verified content: the badges that tell people
 * which collections, questions, models, metrics and dashboards to trust, the
 * admin actions that set them, and the "verified only" filters. Each half does
 * nothing unless the instance has its feature (`official_collections`,
 * `content_verification`), which the backend enforces them with.
 */
export function initializeContentVerificationPlugin() {
  const tokenFeatures = MetabaseSettings.get("token-features");

  if (tokenFeatures?.official_collections) {
    initializeOfficialCollections();
  }

  if (tokenFeatures?.content_verification) {
    initializeVerifiedContent();
  }
}

function initializeOfficialCollections() {
  // Other plugins may give collections other types, so leave those to them.
  const getCollectionTypeFallback = PLUGIN_COLLECTIONS.getCollectionType;

  Object.assign(PLUGIN_COLLECTIONS, {
    AUTHORITY_LEVEL: {
      ...PLUGIN_COLLECTIONS.AUTHORITY_LEVEL,
      official: OFFICIAL_COLLECTION,
    },
    isRegularCollection: (collection) => !isOfficialCollection(collection),
    getCollectionType: (collection) =>
      isOfficialCollection(collection)
        ? OFFICIAL_COLLECTION
        : getCollectionTypeFallback(collection),
    getAuthorityLevelMenuItems,
  } satisfies Partial<typeof PLUGIN_COLLECTIONS>);

  Object.assign(PLUGIN_COLLECTION_COMPONENTS, {
    CollectionAuthorityLevelIcon,
    FormCollectionAuthorityLevelPicker,
    CollectionAuthorityLevelDisplay,
  } satisfies Partial<typeof PLUGIN_COLLECTION_COMPONENTS>);

  PLUGIN_ENTITY_ICON.useGetIcon = createUseGetIcon(
    PLUGIN_ENTITY_ICON.useGetIcon,
  );
}

function initializeVerifiedContent() {
  Object.assign(PLUGIN_MODERATION, {
    isEnabled: () => true,
    EntityModerationIcon,
    ModerationReviewTextForQuestion,
    ModerationReviewTextForDashboard,
    ModerationStatusIcon,
    getStatusIcon,
    getModerationTimelineEvents,
    useCardMenuItems,
    useQuestionMenuItems,
    useDashboardMenuItems,
    MetabotVerifiedContentConfigurationPane,
  } satisfies Partial<typeof PLUGIN_MODERATION>);

  Object.assign(PLUGIN_CONTENT_VERIFICATION, {
    contentVerificationEnabled: true,
    ModelFilterControls,
    MetricFilterControls,
    getDefaultModelFilters,
    getDefaultMetricFilters,
  } satisfies Partial<typeof PLUGIN_CONTENT_VERIFICATION>);

  PLUGIN_SEARCH_FILTERS.VerifiedFilter = VerifiedFilter;
}
